import { openDB } from 'idb';
import { v4 as uuidv4 } from 'uuid';

const DB_NAME = 'inventory_offline';
const DB_VERSION = 1;

class OfflineStorageService {
  constructor() {
    this.db = null;
    this.initDB();
  }

  async initDB() {
    this.db = await openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // 创建离线交易存储
        if (!db.objectStoreNames.contains('transactions')) {
          const store = db.createObjectStore('transactions', { keyPath: 'uuid' });
          store.createIndex('synced', 'synced');
          store.createIndex('created_at', 'created_at');
        }

        // 创建产品缓存
        if (!db.objectStoreNames.contains('products')) {
          db.createObjectStore('products', { keyPath: 'id' });
        }

        // 创建仓库缓存
        if (!db.objectStoreNames.contains('warehouses')) {
          db.createObjectStore('warehouses', { keyPath: 'id' });
        }

        // 创建批次缓存
        if (!db.objectStoreNames.contains('batches')) {
          const batchStore = db.createObjectStore('batches', { keyPath: 'id' });
          batchStore.createIndex('product_id', 'product_id');
          batchStore.createIndex('warehouse_id', 'warehouse_id');
        }
      }
    });
  }

  // 保存离线交易
  async saveTransaction(transaction) {
    const tx = this.db.transaction('transactions', 'readwrite');
    const store = tx.objectStore('transactions');
    
    const record = {
      ...transaction,
      uuid: transaction.uuid || uuidv4(),
      synced: false,
      created_at: new Date().toISOString(),
      local_id: Date.now(),
      retry_count: 0,
      last_error: null
    };
    
    await store.add(record);
    return record;
  }

  async updateTransactionRetry(uuid, errorMessage) {
    const tx = this.db.transaction('transactions', 'readwrite');
    const store = tx.objectStore('transactions');
    
    const transaction = await store.get(uuid);
    if (transaction) {
      transaction.retry_count = (transaction.retry_count || 0) + 1;
      transaction.last_error = errorMessage;
      transaction.last_retry_at = new Date().toISOString();
      await store.put(transaction);
      return transaction.retry_count;
    }
    return 0;
  } 

  // 获取未同步的交易
  async getUnsyncedTransactions() {
    const tx = this.db.transaction('transactions', 'readonly');
    const store = tx.objectStore('transactions');
    const allTransactions = await store.getAll();  
    return allTransactions.filter(transaction => !transaction.synced);
  }

  // 标记交易为已同步
  async markTransactionSynced(uuid) {
    const tx = this.db.transaction('transactions', 'readwrite');
    const store = tx.objectStore('transactions');
    
    const transaction = await store.get(uuid);
    if (transaction) {
      transaction.synced = true;
      transaction.synced_at = new Date().toISOString();
      await store.put(transaction);
    }
  }

  // 删除已同步的交易
  async deleteTransaction(uuid) {
    const tx = this.db.transaction('transactions', 'readwrite');
    const store = tx.objectStore('transactions');
    await store.delete(uuid);
  }

  // 缓存产品数据
  async cacheProducts(products) {
    const tx = this.db.transaction('products', 'readwrite');
    const store = tx.objectStore('products');
    
    for (const product of products) {
      await store.put(product);
    }
  }

  // 获取缓存的产品
  async getCachedProducts() {
    const tx = this.db.transaction('products', 'readonly');
    const store = tx.objectStore('products');
    return await store.getAll();
  }

  // 缓存仓库数据
  async cacheWarehouses(warehouses) {
    const tx = this.db.transaction('warehouses', 'readwrite');
    const store = tx.objectStore('warehouses');
    
    for (const warehouse of warehouses) {
      await store.put(warehouse);
    }
  }

  // 获取缓存的仓库
  async getCachedWarehouses() {
    const tx = this.db.transaction('warehouses', 'readonly');
    const store = tx.objectStore('warehouses');
    return await store.getAll();
  }

  // 缓存批次数据
  async cacheBatches(batches) {
    const tx = this.db.transaction('batches', 'readwrite');
    const store = tx.objectStore('batches');
    
    for (const batch of batches) {
      await store.put(batch);
    }
  }

  // 获取缓存的批次
  async getCachedBatches(filters = {}) {
    const tx = this.db.transaction('batches', 'readonly');
    const store = tx.objectStore('batches');
    
    let batches = await store.getAll();
    
    // 应用过滤器
    if (filters.product_id) {
      batches = batches.filter(b => b.product_id === filters.product_id);
    }
    if (filters.warehouse_id) {
      batches = batches.filter(b => b.warehouse_id === filters.warehouse_id);
    }
    
    return batches;
  }

  // 清理旧的已同步交易
  async cleanupSyncedTransactions(daysToKeep = 7) {
    const tx = this.db.transaction('transactions', 'readwrite');
    const store = tx.objectStore('transactions');
    
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
    
    const allTransactions = await store.getAll();
    
    for (const transaction of allTransactions) {
      if (transaction.synced && 
          new Date(transaction.synced_at) < cutoffDate) {
        await store.delete(transaction.uuid);
      }
    }
  }

  // 清理过期的失败交易
  async cleanupFailedTransactions(maxRetries = 3, maxAgeDays = 7) {
    const tx = this.db.transaction('transactions', 'readwrite');
    const store = tx.objectStore('transactions');
    
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - maxAgeDays);
    
    const allTransactions = await store.getAll();
    let cleanedCount = 0;
    
    for (const transaction of allTransactions) {
      const shouldDelete = 
        // 重试次数过多
        (transaction.retry_count >= maxRetries) ||
        // 创建时间过久且未同步
        (!transaction.synced && new Date(transaction.created_at) < cutoffDate) ||
        // 有致命错误标记
        (transaction.fatal_error === true);
        
      if (shouldDelete) {
        await store.delete(transaction.uuid);
        cleanedCount++;
      }
    }
    
    return cleanedCount;
  }  
}

export default new OfflineStorageService();

if (typeof window !== 'undefined' && 'indexedDB' in window) {
} else {
  console.warn('IndexedDB not supported, offline features will be limited');
}